import React from 'react';
import { CheckpointCardProps, CheckpointCardWrapperProps } from './types';
import style from './style.module.scss';
import cx from 'classnames';
import RandomIllustration from '../../utils/random_illustration';
import { DefaultButton, PrimaryButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { Row, Col } from 'react-bootstrap';
import Icon from '../../assets/icons';
import { PremiumBadge } from '@magoosh/lib/info_badges';
import ConditionalWrapper from '../../utils/conditional_wrapper';
import moment from 'moment';

export const CheckpointCardWrapper: React.FC<CheckpointCardWrapperProps> = (props) => {
  const { illustrations, children } = props;

  return (
    <div>
      <Row>
        <Col lg={5} xsHidden>
          <div className={style.illustration}>
            <RandomIllustration illustrations={illustrations} />
          </div>
        </Col>
        <Col lg={7}>
          <div className="u-margin-H-s">{children}</div>
        </Col>
      </Row>
    </div>
  );
};

export const CheckpointCard: React.FC<CheckpointCardProps> = (props) => {
  const {
    iconType,
    illustrations,
    headline,
    estimatedTime,
    running,
    locked,
    scored,
    date,
    includesInfoIcon,
    percentageCompleted,
    copy,
    primaryCTA,
    secondaryCTA,
    footer,
    isWrapped,
    children
  } = props;

  return (
    <div className={cx(style.checkpointCard, isWrapped && style.checkpointCardWrapper, 'u-margin-B-l')}>
      <ConditionalWrapper
        condition={isWrapped}
        wrapper={(children) => (
          <CheckpointCardWrapper illustrations={illustrations}>
            {children}
          </CheckpointCardWrapper>
        )}>
        {iconType && <Icon iconType={iconType} fill={style.brandPrimary} />}
        {locked && (
          <span>
            <PremiumBadge style={{ float: 'right' }} className={'u-margin-H-xs'} />
          </span>
        )}
        <h3 className="u-margin-V-xs">{isWrapped ? <strong>{headline}</strong> : headline}</h3>
        <p style={{ color: style.gray }}>
          {estimatedTime && (
            <span className="u-margin-R-xs">
              <FontAwesomeIcon icon={'clock-o'} /> {estimatedTime} {scored && <span>{scored}</span>}
            </span>
          )}

          {running && (
            <span className="u-margin-R-xs">
              <FontAwesomeIcon icon={'spinner'} /> Incomplete
            </span>
          )}

          {date && (
            <span className="u-margin-R-xs">
              <FontAwesomeIcon icon={'calendar-check-o'} /> {moment(date).format('L')}{' '}
              {includesInfoIcon && <FontAwesomeIcon icon={'info-circle'} />}
            </span>
          )}

          {percentageCompleted && (
            <span className="u-margin-R-xs">
              <FontAwesomeIcon icon={'check'} /> percentageCompleted
            </span>
          )}
        </p>
        {copy && <p>{copy}</p>}

        {children}

        <div className={cx(style.buttonFooter, 'u-margin-B-m')}>
          {primaryCTA && primaryCTA.variant === 'primary' && (
            <PrimaryButton
              className="u-margin-R-xs"
              size="md"
              disabled={primaryCTA.isDisabled}
              {...primaryCTA}>
              {primaryCTA.text}
            </PrimaryButton>
          )}

          {primaryCTA && primaryCTA.variant === 'secondary' && (
            <SecondaryButton
              className="u-margin-R-xs"
              size="md"
              disabled={primaryCTA.isDisabled}
              {...primaryCTA}>
              {primaryCTA.text}
            </SecondaryButton>
          )}

          {primaryCTA && primaryCTA.variant === 'default' && (
            <DefaultButton
              className="u-margin-R-xs"
              size="md"
              disabled={primaryCTA.isDisabled}
              {...primaryCTA}>
              {primaryCTA.text}
            </DefaultButton>
          )}

          {secondaryCTA && secondaryCTA.variant === 'secondary' && (
            <a style={{ color: style.brandSecondary }} {...secondaryCTA}>
              <strong>{secondaryCTA.text}</strong>
            </a>
          )}

          {secondaryCTA && secondaryCTA.variant === 'default' && (
            <a style={{ color: style.grayDarker }} {...secondaryCTA}>
              {secondaryCTA.text}
            </a>
          )}
        </div>
        {footer && (
          <p className={style.footer}>
            <small>{footer}</small>
          </p>
        )}
      </ConditionalWrapper>
    </div>
  );
};

export default CheckpointCard;
